import React from 'react';
import {Helmet} from 'react-helmet-async';
import {Link} from 'react-router-dom';
import {citiesWithPublishedGuides} from '../data/cityData';
import soltLogo from '../assets/logo.png';
import appleIconWhite from '../assets/apple-icon-white.png';
import {BRAND_TAGLINE, BRAND_TITLE} from '../constants/brand';
import {APP_STORE_URL} from '../constants/app';
import './Home.css';

const SITE_ORIGIN = 'https://solt.app';
const PAGE_DESCRIPTION =
    'See what people actually ate before you pick a restaurant. Real dish photos from friends and local foodies—not just star ratings.';

const WHY_SOLT = [
    {
        title: 'Dish-first',
        body: 'See the meal, not just the venue. Know what to order before you book.',
        icon: (
            <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
                <circle cx="24" cy="24" r="16" fill="#FFD9B3"/>
                <circle cx="24" cy="24" r="10" fill="#F08A3E"/>
                <circle cx="20" cy="20" r="2" fill="#fff" opacity="0.6"/>
                <circle cx="28" cy="22" r="1.5" fill="#fff" opacity="0.4"/>
            </svg>
        ),
    },
    {
        title: 'Real people',
        body: 'Local foodies who ate there—not anonymous review farms.',
        icon: (
            <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
                <circle cx="17" cy="20" r="7" fill="#FBC9C9"/>
                <path d="M6 38c0-6 5-10 11-10s11 4 11 10" fill="#FBC9C9"/>
                <circle cx="32" cy="22" r="6" fill="#F47C7C"/>
                <path d="M22 38c0-5 4.5-9 10-9s10 4 10 9" fill="#F47C7C"/>
            </svg>
        ),
    },
    {
        title: 'Community-driven',
        body: 'Rankings from real Solt eats. No pay-to-list on city guides.',
        icon: (
            <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
                <path
                    d="M24 8l4.5 9.2 10.2 1.5-7.4 7.2 1.8 10.1L24 31.3 14.9 36l1.8-10.1-7.4-7.2 10.2-1.5L24 8z"
                    fill="#FFD66B"
                    stroke="#E0A800"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                />
            </svg>
        ),
    },
] as const;

const PRODUCT_FEATURES = [
    {
        id: 'discover',
        title: 'Browse places near you',
        subtitle:
            'Scroll dish photos from friends and foodies—like a feed built around what people actually ordered.',
        image: '/solt-discover.PNG',
        imageAlt: 'Discover places to eat on Solt',
        reverse: false,
    },
    {
        id: 'save',
        title: 'Save dishes you want to try',
        subtitle: 'Build a shortlist of spots and plates for your next lunch, date, or trip.',
        image: '/solt-save.PNG',
        imageAlt: 'Save restaurants and dishes in Solt',
        reverse: true,
    },
    {
        id: 'track',
        title: 'Build your foodie profile.',
        subtitle: 'Log what you have tried and keep coming back to the places you love.',
        image: '/solt-profile.PNG',
        imageAlt: 'Your food profile on Solt',
        reverse: false,
    },
] as const;

const homeStructuredData = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'WebSite',
            '@id': `${SITE_ORIGIN}/#website`,
            url: `${SITE_ORIGIN}/`,
            name: 'Solt',
            description: PAGE_DESCRIPTION,
            inLanguage: 'en',
            publisher: {'@id': `${SITE_ORIGIN}/#organization`},
        },
        {
            '@type': 'Organization',
            '@id': `${SITE_ORIGIN}/#organization`,
            name: 'Solt',
            url: SITE_ORIGIN,
            sameAs: [APP_STORE_URL],
        },
        {
            '@type': 'SoftwareApplication',
            name: 'Solt',
            operatingSystem: 'iOS',
            applicationCategory: 'FoodApplication',
            offers: {
                '@type': 'Offer',
                price: '0',
                priceCurrency: 'USD',
            },
            downloadUrl: APP_STORE_URL,
        },
    ],
};

function AppStoreButton({className}: {className?: string}) {
    return (
        <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`landing-app-store-link ${className ?? ''}`.trim()}
        >
            <button type="button" className="ios-button">
                <img src={appleIconWhite} alt=""/>
                Download on the App Store
            </button>
        </a>
    );
}

const Home: React.FC = () => {
    return (
        <>
            <Helmet>
                <title>{BRAND_TITLE}</title>
                <meta name="description" content={PAGE_DESCRIPTION}/>
                <link rel="canonical" href={`${SITE_ORIGIN}/`}/>
                <meta property="og:title" content={BRAND_TITLE}/>
                <meta property="og:description" content={PAGE_DESCRIPTION}/>
                <meta property="og:url" content={`${SITE_ORIGIN}/`}/>
                <meta property="og:type" content="website"/>
                <meta property="og:image" content={`${SITE_ORIGIN}/solt-main-og3.jpg`}/>
                <meta name="twitter:card" content="summary_large_image"/>
                <meta name="twitter:title" content={BRAND_TITLE}/>
                <meta name="twitter:description" content={PAGE_DESCRIPTION}/>
                <meta name="twitter:image" content={`${SITE_ORIGIN}/solt-main-og3.jpg`}/>
                <script type="application/ld+json">{JSON.stringify(homeStructuredData)}</script>
            </Helmet>

            <div className="landing-home-container">
                <header className="landing-home-header">
                    <div className="landing-home-header-top">
                        <Link to="/" aria-label="Solt home">
                            <img src={soltLogo} alt="Solt logo" className="landing-solt-logo"/>
                        </Link>
                        <a
                            href={APP_STORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="landing-header-cta"
                        >
                            Get the app
                        </a>
                    </div>
                </header>

                <div className="bar"/>

                <section className="landing-hero-section" aria-labelledby="landing-hero-heading">
                    <div className="landing-hero-left">
                        <p className="landing-hero-eyebrow">Solt — {BRAND_TAGLINE}</p>
                        <h1 id="landing-hero-heading" className="landing-hero-title">
                            See what people actually ate.
                        </h1>
                        <p className="landing-hero-subtext">
                            Dish-first discovery from friends and local foodies. Save spots, track your
                            own eats, and know what to order before you go.
                        </p>
                        <div className="landing-hero-buttons">
                            <AppStoreButton/>
                        </div>
                        <p className="landing-ios-note">Available on iOS. Free to download.</p>
                    </div>

                    <div className="landing-phone-wrap">
                        <img
                            src="/solt-friends.PNG"
                            alt="Solt app showing eats from foodies you follow"
                            className="landing-phone-img"
                            width={320}
                            height={640}
                            fetchPriority="high"
                        />
                    </div>
                </section>

                <section className="landing-why" aria-labelledby="landing-why-heading">
                    <h2 id="landing-why-heading" className="landing-why-label">
                        Why Solt
                    </h2>
                    <p className="landing-why-hook">
                        Other apps tell you where. Solt shows you what.
                    </p>
                    <ul className="landing-why-list">
                        {WHY_SOLT.map((item) => (
                            <li key={item.title} className="landing-why-item">
                                <span className="landing-why-icon">{item.icon}</span>
                                <h3>{item.title}</h3>
                                <p>{item.body}</p>
                            </li>
                        ))}
                    </ul>
                </section>

                {PRODUCT_FEATURES.map((feature) => (
                    <section
                        key={feature.id}
                        className={`landing-feature-section${
                            feature.reverse ? ' landing-feature-section-reverse' : ''
                        }`}
                        aria-labelledby={`feature-${feature.id}`}
                    >
                        <div className="landing-feature-text">
                            <h2 id={`feature-${feature.id}`} className="landing-feature-title">
                                {feature.title}
                            </h2>
                            <p className="landing-feature-subtitle">{feature.subtitle}</p>
                        </div>
                        <div className="landing-phone-wrap">
                            <img
                                src={feature.image}
                                alt={feature.imageAlt}
                                className="landing-phone-img"
                                loading="lazy"
                                width={300}
                                height={600}
                            />
                        </div>
                    </section>
                ))}

                {citiesWithPublishedGuides.length > 0 && (
                    <section
                        className="landing-guides-section"
                        aria-labelledby="landing-guides-heading"
                    >
                        <h2 id="landing-guides-heading" className="landing-guides-title">
                            Planning where to eat?
                        </h2>
                        <p className="landing-guides-lead">
                            City guides built from real Solt eats—sorted by what local foodies share,
                            not sponsored placement.
                        </p>
                        <ul className="landing-guides-list">
                            {citiesWithPublishedGuides.map((city) => (
                                <li key={city.hubHref} className="landing-guides-item">
                                    <span className="landing-guides-city">{city.name}</span>
                                    <Link to={city.hubHref}>Where to eat</Link>
                                    <Link to={city.bestRestaurantsHref}>Best restaurants</Link>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                <section className="landing-cta-band" aria-labelledby="landing-cta-heading">
                    <h2 id="landing-cta-heading" className="landing-cta-title">
                        Know what to order before you go.
                    </h2>
                    <AppStoreButton/>
                    <p className="landing-ios-note landing-ios-note--centered">
                        Available on iOS. Free to download.
                    </p>
                </section>

                <footer className="landing-home-footer">
                    <div className="landing-home-footer-inner">
                        <div className="bar"/>
                        <p className="landing-home-footer-note">Made in Europe. ❤️</p>
                        <p className="landing-home-footer-contact">
                            Say hi: <a href="mailto:hello@solt.app">hello@solt.app</a>
                        </p>
                        <nav className="landing-home-footer-nav" aria-label="Footer">
                            <Link to="/privacy">Privacy</Link>
                            <Link to="/terms">Terms</Link>
                            <Link to="/cookies">Cookies</Link>
                            <Link to="/sitemap">Sitemap</Link>
                        </nav>
                    </div>
                </footer>
            </div>
        </>
    );
};

export default Home;
